'use client';
import { type FC, Fragment, useMemo } from 'react';
import { DeskDashboardPanel } from '@/components/DeskDashboard';
import { LabelsSection } from '@/components/LabelsSection';
import { Skeleton } from '@/components/Skeleton';
import { useLabels } from '@/hooks/labels';
import {
  BonanzaPromoDashboardContent,
  BonanzaPromoSnackbar,
  usePromotionsAvailability,
} from '@/modules/promotions';
import { useActiveHeadlinePromoConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/useActiveHeadlinePromoConfig';
import { useGetCommonStore } from '@/store';
import { ScRelativeContainer } from '@/styles/common';
import { DepositPanel } from '../UserBlock';
import { BonanzaPromoSection } from './BonanzaPromoSection';
import { DiceRollPromoSection } from './DiceRollPromoSection';
import { FortuneWheelPromoSection } from './FortuneWheelPromoSection';
import { HeadlinePromoSection } from './HeadlinePromoSection';
import { LoyaltyProgramPopup } from './LoyaltyProgramSection/LoyaltyProgramPopup';
import type { LoyaltySectionProps } from './LoyaltySection';
import { MissionPromoSection } from './MissionPromoSection';
import { QuestPromoSection } from './QuestPromoSection';
import { ScratchPromoSection } from './ScratchPromoSection';
import { ScDashboardPanelPopup } from './styled';

export const BonanzaPromoDashboardDesktopPanel = () => {
  const useCommonStore = useGetCommonStore();

  const bonanzaPromoMenuData = useCommonStore.use.bonanzaPromoMenuData();
  const pendingBonanzaPromoMenuData =
    useCommonStore.use.pendingBonanzaPromoMenuData();

  if (pendingBonanzaPromoMenuData)
    return <Skeleton.Rect height={48} rounded width={120} />;

  if (!bonanzaPromoMenuData) return;

  return (
    <ScRelativeContainer>
      <DeskDashboardPanel
        content={<BonanzaPromoDashboardContent />}
        popupContent={
          <ScDashboardPanelPopup>
            <BonanzaPromoSection />
          </ScDashboardPanelPopup>
        }
      />
      <BonanzaPromoSnackbar />
    </ScRelativeContainer>
  );
};

export const DashboardPanelMobile: FC<
  Pick<LoyaltySectionProps, 'onLinkClick'>
> = ({ onLinkClick }) => {
  const useCommonStore = useGetCommonStore();

  const loyaltyData = useCommonStore.use.getLoyaltyData()();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const activeHeadlinePromoConfig = useActiveHeadlinePromoConfig();

  const {
    isQuestAvailable,
    isDiceRollAvailable,
    isBonanzaPromoAvailable,
    isFortuneWheelAvailable,
    isScratchGameAvailable,
    isMissionAvailable,
  } = usePromotionsAvailability();
  const labels = useLabels(true);

  const activeItems = useMemo(() => {
    const headlinePromoItem = activeHeadlinePromoConfig && {
      id: `headline-promo:${activeHeadlinePromoConfig.promoKey}`,
      component: (
        <HeadlinePromoSection
          config={activeHeadlinePromoConfig}
          onLinkClick={onLinkClick}
        />
      ),
      shouldRender: true,
    };

    const allPanelItems = [
      {
        id: 'deposit',
        component: (
          <div className="content user-block">
            <DepositPanel boldValue mobile onLinkClick={onLinkClick} />
          </div>
        ),
        shouldRender: isAuthenticated,
      },
      {
        id: 'loyalty-program',
        component: <LoyaltyProgramPopup onLinkClick={onLinkClick} />,
        shouldRender: isAuthenticated && loyaltyData,
      },
      {
        id: 'bonanza',
        component: <BonanzaPromoSection isMobile onLinkClick={onLinkClick} />,
        shouldRender: isBonanzaPromoAvailable,
      },
      {
        id: 'fortune',
        component: <FortuneWheelPromoSection onLinkClick={onLinkClick} />,
        shouldRender: isAuthenticated && isFortuneWheelAvailable,
      },
      {
        id: 'dice',
        component: <DiceRollPromoSection onLinkClick={onLinkClick} />,
        shouldRender: isAuthenticated && isDiceRollAvailable,
      },
      {
        id: 'quest',
        component: <QuestPromoSection onLinkClick={onLinkClick} />,
        shouldRender: isQuestAvailable,
      },
      {
        id: 'scratch',
        component: <ScratchPromoSection onLinkClick={onLinkClick} />,
        shouldRender: isAuthenticated && isScratchGameAvailable,
      },
      {
        id: 'mission',
        component: <MissionPromoSection onLinkClick={onLinkClick} />,
        shouldRender: isMissionAvailable,
      },
      ...(headlinePromoItem ? [headlinePromoItem] : []),
      {
        id: 'labels',
        component: <LabelsSection isDashboard onLabelClick={onLinkClick} />,
        shouldRender: isAuthenticated && labels.length,
      },
    ];

    return allPanelItems.filter((item) => item.shouldRender);
  }, [
    isAuthenticated,
    onLinkClick,
    loyaltyData,
    isBonanzaPromoAvailable,
    isFortuneWheelAvailable,
    isDiceRollAvailable,
    isQuestAvailable,
    isScratchGameAvailable,
    isMissionAvailable,
    activeHeadlinePromoConfig,
    labels.length,
  ]);

  if (!activeItems.length) return null;

  return (
    <ScDashboardPanelPopup isMobile>
      {activeItems.map((item, index) => (
        <Fragment key={item.id}>
          {item.component}
          {index < activeItems.length - 1 && <div className="divider" />}
        </Fragment>
      ))}
    </ScDashboardPanelPopup>
  );
};
